/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {Day, Hour, Module} from "@intuitionrobotics/ts-common";
import {ReactNode} from "react";
import {scaleLinear} from "d3-scale";
import dayjs from "../utils/dayjs";

export type Sizing = { height: number, width: number }

export type Coordinates = {
    x: number,
    y: number,
    _id: string
}

export type D3ChartData = {
    label: string | number | ReactNode,
    data: Coordinates[],
    color: string,
    baseValue?: number,
}

export type D3TableData = {
    label: string | number | ReactNode,
    data: Coordinates,
    baseValue?: number
}

export type AxesLabels = {
    x?: string[],
    y?: string[]
}

export type Frames = {
    startFrame?: number
    endFrame?: number
    min?: number,
    max?: number
}

class GraphsModule_Class
    extends Module {

    public tonightAtMidnight: number;
    public todayAtNoon: number;

    constructor() {
        super("GraphsModule");
        this.tonightAtMidnight = dayjs().startOf("day").valueOf();
        this.todayAtNoon = this.tonightAtMidnight + (12 * Hour);
    }

    // private days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    extent = (domain: Coordinates[], frames?: Frames) => {
        let minX = Number.MAX_VALUE;
        let maxX = Number.MIN_VALUE;
        let minY = frames?.startFrame !== undefined ? frames?.startFrame : Number.MAX_VALUE;
        let maxY = frames?.endFrame !== undefined ? frames?.endFrame : Number.MIN_VALUE;
        domain.forEach(_xy => {
            if (!_xy)
                return;
            if (_xy.x < minX)
                minX = _xy.x;
            if (_xy.y < minY) {
                if (!frames?.min)
                    return minY = _xy.y;
                if (frames.min < _xy.y)
                    minY = _xy.y;
            }
            if (_xy.x > maxX)
                maxX = _xy.x;
            if (_xy.y > maxY) {
                if (!frames?.max)
                    return maxY = _xy.y;
                if (frames.max > _xy.y)
                    maxY = _xy.y;
            }
        });
        return {minX, maxX, minY, maxY};
    };

    scale = (startDomain: number, endDomain: number, startRange: number, endRange: number) => {
        return scaleLinear()
            .domain([startDomain, endDomain])
            .range([startRange, endRange]);
    };

    yScale = (domainX: number, domainY: number, height: number) => scaleLinear()
        .domain([domainX, domainY])
        .range([height, 0]);

    checkRange = (point: number, currentEndDate: number, viewBox: number, frequency: number) => {
        return point <= currentEndDate && point > (currentEndDate - (viewBox * frequency) + (frequency / 2));
    };

    getAxesLabels = (axesNumbers: number[], frequency?: number) => {
        if (!frequency || frequency === Day)
            return axesNumbers.map(_x => {
                return this.getAxesLabel(_x);
            });
        return this.getWeeklyLabel(axesNumbers);
    };

    getAxesLabel = (axesNumber: number): string => {
        // To avoid the timezone issue, we are using the moment library to format the date
        // and shift to midday in order to avoid collisions with daylight saving time change
        const dateString = dayjs(axesNumber - Hour * 12);
        return dateString.format("M/D");
        // // const day = this.days[dateString.getDay()];
        // // it was: const dateArray = dateString.toLocaleDateString().split('/');
        // const dateArray = dateString.format(DayFormat).split('/');
        // // Not sure...
        // if(dateArray.length < 2)
        // 	return dateString.format(DayFormat);
        //
        // return `${dateArray[1]}/${dateArray[0]}`;
    };

    getWeeklyLabel = (axesNumbers: number[]) => {
        const axesLabels = [];
        for (let i = 0; i < axesNumbers.length; i = i + 7) {
            const start = this.getAxesLabel(axesNumbers[i]);
            let end = "";
            if (i + 6 < axesNumbers.length)
                end = this.getAxesLabel(axesNumbers[i + 6]);
            else end = this.getAxesLabel(axesNumbers[axesNumbers.length - 1]);
            axesLabels.push(`${start}-${end}`);
        }
        return axesLabels;
    };

    getEndDate = () => dayjs().endOf("day").valueOf()

}

export const GraphsModule = new GraphsModule_Class();
